import { useEffect, useRef, useState } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useParams, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { ChatUnreadMessagesCount } from "@/components/chat/ChatUnreadMessagesCount";
import { MousePositionContextMenu } from "@/components/sidebar/context-menu/MousePositionContextMenu";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";
import { useHideLockInPublicProfile } from "@/utils/useShowPublicProfile";

import { useRowFocusWithin } from "../useRowFocusWithin";
import { TreeItem } from "../useSidebarNav";

import { ContextMenu } from "./ContextMenu";
import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";
import { ListItemTitle } from "./ListItemTitle";
import { LockIndicatorMenu } from "./LockIndicatorMenu";
import { useDocumentItemMenuItems } from "./useDocumentItemMenuItems";

import type { RenderContext } from "./FolderItem";
import type { DocumentShort } from "@/api";

export const DocItem = observer(function DocItem({
  docItem,
  className,
  isOverlay,
  parentId,
  position,
  draggable = true,
  sortable = true,
  isInTrash = false,
  invertedColor = true,
  renderContext,
  isMobile,
}: {
  docItem: DocumentShort;
  className?: string;
  isOverlay?: boolean;
  position?: number;
  parentId?: string;
  draggable?: boolean;
  sortable?: boolean;
  isInTrash?: boolean;
  invertedColor?: boolean;
  renderContext?: RenderContext;
  isMobile?: boolean;
}) {
  const { docId } = useParams<{ [key: string]: string | undefined }>();
  const { documentTree, view } = useStore();
  const t = useTranslations();
  const { uid, title, importInProgress, unreadMessagesCount } = docItem;
  const isActive = documentTree.isCurrentDoc(docItem) || docId === uid;
  const searchParams = useSearchParams();
  const preview = !!searchParams.get("preview");
  const href = docPageUrl({
    docId: uid,
    preview,
  });

  const [hover, setHover] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [lockMenuOpen, setLockMenuOpen] = useState(false);
  const { focusWithin, focusWithinProps } = useRowFocusWithin();
  const showMenu = hover || focusWithin || isMobile || contextMenuOpen;

  const itemRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const hasMounted = useRef(false);
  const needsScroll = isActive && !isMobile;

  useEffect(() => {
    if (needsScroll) {
      const behavior = hasMounted.current ? "smooth" : "instant";
      const block = hasMounted.current ? "nearest" : "center";

      itemRef.current?.scrollIntoView({ behavior, block });
    }
    hasMounted.current = true;
  }, [needsScroll]);

  const { handlerProps, tooltip } = useTooltip({
    title: title || t("doc.documentTitle.placeholder"),
    placement: "right",
  });

  const showTooltip = hover && !isOverlay;

  const allItems = useDocumentItemMenuItems({
    documentTree,
    docItem,
    uid,
    renderContext,
    onLockedChange: (locked) => documentTree.setDocumentLocked(uid, locked),
  });
  const items = isInTrash ? [] : allItems;
  const hideLockInPublicProfile = useHideLockInPublicProfile(docItem.userPermissions.write);
  // Keyboard focus hides the lock with CSS (LockIndicatorMenu), so a clicked row keeps it
  const showLockIndicator =
    !!docItem.locked &&
    !hideLockInPublicProfile &&
    !isInTrash &&
    !isOverlay &&
    (lockMenuOpen || !(hover || isMobile || contextMenuOpen));

  if (importInProgress) {
    return (
      <div
        className={twMerge(
          "px-2.5 py-3.5 rounded-8 bg-white dark:bg-black flex flex-col",
          "text-[15px] leading-[18px]",
        )}
      >
        <span className="font-semibold">{t("documentTree.importingDocument.title")}:</span>
        <span className="font-semibold">{title || t("doc.documentTitle.placeholder")}</span>
        <div className="w-full h-0.5 mt-2.5 bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div className="h-full w-2/5 bg-blue-500 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <Draggable
      id={uid}
      type="file"
      parentId={parentId}
      position={position}
      isOverlay={isOverlay}
      disabled={!draggable}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      {/* Drop before file */}
      <Droppable
        id={uid}
        type="file"
        parentId={parentId}
        position={position}
        disabled={!draggable || !sortable}
      >
        <MousePositionContextMenu items={items} onOpenChange={setContextMenuOpen}>
          <TreeItem
            ref={itemRef}
            focused={focusWithin}
            {...focusWithinProps}
            aria-label={title || t("doc.documentTitle.placeholder")}
            onActivate={() => linkRef.current?.click()}
            className={twMerge(
              "relative flex group rounded-6 cursor-pointer select-none",
              isActive && "border-blue-100 dark:border-blue-900 bg-blue-100 dark:bg-blue-900",
              !isOverlay && invertedColor
                ? "hover:bg-sidebar-button-hover-inverted-bg [&:has(button[data-open])]:bg-sidebar-button-hover-inverted-bg sm:hover:bg-transparent"
                : "hover:bg-sidebar-button-hover-bg [&:has(button[data-open])]:bg-sidebar-button-hover-bg sm:hover:bg-transparent",
              contextMenuOpen &&
                (invertedColor
                  ? "bg-sidebar-button-hover-inverted-bg"
                  : "bg-sidebar-button-hover-bg"),
              className,
            )}
          >
            <Link
              ref={linkRef}
              tabIndex={-1}
              href={href}
              aria-current={isActive ? "page" : undefined}
              className={twMerge(
                "flex items-center w-full min-h-7.5 pl-[22px] pr-5",
                "fromSm:group-hover:pr-7 fromSm:group-has-[:focus-visible]:pr-7 sm:pr-10",
                showLockIndicator && "fromSm:pr-7",
                unreadMessagesCount > 0 &&
                  "fromSm:pr-7 fromSm:group-hover:pr-14 fromSm:group-has-[:focus-visible]:pr-14 fromSm:group-has-[button[data-open]]:pr-14 sm:!pr-20",
                showLockIndicator && unreadMessagesCount > 0 && "fromSm:pr-14",
              )}
              onClick={() => {
                if (isMobile && isActive) {
                  runInAction(() => {
                    view.isMobileDocsVisible = false;
                  });
                }
              }}
              prefetch={false}
              {...handlerProps}
            >
              <ListItemTitle type="document" title={title || ""} />
            </Link>
            {showMenu && !isInTrash && !isOverlay && (
              <ContextMenu
                items={items}
                title={title || t("doc.documentTitle.placeholder")}
                rowRef={itemRef}
                onOpenChange={setContextMenuOpen}
              />
            )}
            {showLockIndicator && (
              <LockIndicatorMenu
                items={items}
                onOpenChange={(open) => {
                  setLockMenuOpen(open);
                  setContextMenuOpen(open);
                }}
              />
            )}
            {!isOverlay && (
              <ChatUnreadMessagesCount
                count={unreadMessagesCount}
                className={twMerge(
                  "absolute top-1/2 -translate-y-1/2 right-2.5 group-has-[button[data-open]]:right-8",
                  renderContext === "document-tree" &&
                    "group-hover:right-8 group-has-[:focus-visible]:right-8 sm:!right-12",
                  showLockIndicator && "right-8",
                )}
              />
            )}
            {showTooltip && tooltip}
          </TreeItem>
        </MousePositionContextMenu>
      </Droppable>
    </Draggable>
  );
});
